/**
 * @voltxampere/engine/mcu — AVR-8bit ISA subset emulator (Sprint 46-4)
 *
 * Per ADR-0068, this is the MVP subset emulator: ~30 core opcodes
 * (ALU + data transfer + branch + bit + NOP) plus GPIO + ADC + PWM
 * peripheral registers. Sufficient for Arduino blink + analogRead +
 * analogWrite. Out of scope (Sprint 47-50+):
 *
 *   - Multiplication (MUL, MULS, MULSU, FMUL*)
 *   - Interrupts (vector table, RETI extended, ISR)
 *   - I2C (TWBR/TWCR/TWSR/TWDR/TWAR/TWAMR) → Sprint 47
 *   - SPI (SPCR/SPSR/SPDR) → Sprint 47
 *   - UART (UDR0/UCSR0A/UCSR0B/UCSR0C/UBRR0H/UBRR0L) → Sprint 48
 *   - Sleep (SLEEP/SMCR) → Sprint 50+
 *   - Watchdog (WDR/WDTCSR) → Sprint 50+
 *   - Timer1/Timer2 advanced PWM → Sprint 50+
 *
 * The emulator state object is plain data; no class, no closures.
 * Sprint 47-2 stamp integration will create one state per MCU
 * component instance and persist it across Newton iterations
 * (state outlives a single solver call).
 *
 * Reference: Atmel AVR Instruction Set Manual (rev 0856-AVR-04/2021),
 * Microchip ATmega328P-PU datasheet §8 (Instruction Set Summary).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Status register flag bit positions per ATmega328P §3.2.1. */
const SREG_C = 0; // Carry
const SREG_Z = 1; // Zero
const SREG_N = 2; // Negative
const SREG_V = 3; // Two's complement overflow
// const SREG_S = 4; // Sign (S = N XOR V) — derived, set when N or V change
// const SREG_H = 5; // Half-carry — Sprint 47+ MUL family
// const SREG_T = 6; // Bit copy storage — Sprint 47+ BLD/BST
const SREG_I = 7; // Global interrupt enable (Sprint 48 ADR-0070)
/**
 * I/O register addresses (ATmega328P data memory map §13.4). The
 * emulator's `ioRegs` array is indexed by `addr - 0x20` (the I/O
 * address space starts at 0x20 in the unified data memory map).
 */
const IO_OFFSET = 0x20;
/** PINB at I/O address 0x03 (data 0x23). */
const IOREG_PINB = 0x23 - IO_OFFSET;
/** DDRB at I/O address 0x04 (data 0x24). */
const IOREG_DDRB = 0x24 - IO_OFFSET;
/** PORTB at I/O address 0x05 (data 0x25). */
const IOREG_PORTB = 0x25 - IO_OFFSET;
/** PINC at I/O address 0x06 (data 0x26). */
const IOREG_PINC = 0x26 - IO_OFFSET;
/** DDRC at I/O address 0x07 (data 0x27). */
const IOREG_DDRC = 0x27 - IO_OFFSET;
/** PORTC at I/O address 0x08 (data 0x28). */
const IOREG_PORTC = 0x28 - IO_OFFSET;
/** PIND at I/O address 0x09 (data 0x29). */
const IOREG_PIND = 0x29 - IO_OFFSET;
/** DDRD at I/O address 0x0A (data 0x2A). */
const IOREG_DDRD = 0x2a - IO_OFFSET;
/** PORTD at I/O address 0x0B (data 0x2B). */
const IOREG_PORTD = 0x2b - IO_OFFSET;
// Sprint 47 PWM/ADC peripheral I/O register addresses (TCCR0A/B, TCNT0,
// OCR0A/B, ADCL, ADCH, ADCSRA, ADMUX) ship as raw data-memory addresses
// in `AVR_IO_ADDRESSES` (export below). Sprint 46 stamp is a no-op so
// these registers are only read/written by `readIORegister` /
// `writeIORegister` test helpers via the unified IO_OFFSET path. Sprint
// 47-2 emulator integration adds dedicated peripheral state machines
// (Timer0 free-running counter, ADC sample-and-conversion FSM) that
// will reintroduce per-register const offsets.
/** I/O register space size — 224 bytes (0x20..0xFF). */
const IO_REGS_SIZE = 0x100 - IO_OFFSET;
/**
 * Construct a fresh AVR emulator state with zeroed registers, SRAM,
 * and I/O regs. The flash array is allocated to `flashWords` 16-bit
 * words (32 KB / 2 = 16384 for ATmega328P).
 */
export function createAvrState(flashWords, sramBytes) {
    return {
        regs: new Uint8Array(32),
        sram: new Uint8Array(sramBytes),
        flash: new Uint16Array(flashWords),
        ioRegs: new Uint8Array(IO_REGS_SIZE),
        sp: sramBytes - 1, // initial SP = end of SRAM (post-reset default)
        pc: 0,
        sreg: 0,
        cycles: 0,
        instructions: 0,
        halted: false,
        lastUnknownOpcode: 0,
        peripheralInterruptFlags: 0,
        peripheralInterruptEnable: 0,
        adcInputVoltages: new Float64Array(8),
        uartTxBuffer: [],
        uartRxBuffer: [],
        uartBaudRate: 0,
    };
}
/**
 * Load a flash byte stream into the emulator's flash word array. The
 * Intel HEX parser produces a `Uint8Array`; this helper packs the
 * little-endian byte pairs into 16-bit instruction words.
 */
export function loadFlashBytes(state, flashBytes) {
    const wordCount = Math.min(state.flash.length, flashBytes.length >> 1);
    for (let i = 0; i < wordCount; i += 1) {
        const lo = flashBytes[i * 2] ?? 0;
        const hi = flashBytes[i * 2 + 1] ?? 0;
        state.flash[i] = (hi << 8) | lo;
    }
    // Zero-fill any remaining flash so old code from a previous .hex
    // upload does not leak into the new program's address space.
    for (let i = wordCount; i < state.flash.length; i += 1) {
        state.flash[i] = 0;
    }
}
/**
 * Read an I/O register through the unified data memory map. `addr`
 * is the data memory address (0x20..0xFF for I/O space). Returns 0
 * for addresses outside the I/O range.
 */
function readIO(state, addr) {
    if (addr < IO_OFFSET || addr >= 0x100)
        return 0;
    const offset = addr - IO_OFFSET;
    // Sprint 49 ADR-0071 — UART RX FIFO pop on UDR0 read.
    if (offset === IOREG_UDR0) {
        const byte = state.uartRxBuffer.shift() ?? 0;
        if (state.uartRxBuffer.length === 0) {
            state.ioRegs[IOREG_UCSR0A] = (state.ioRegs[IOREG_UCSR0A] ?? 0) & ~UCSR0A_RXC;
        }
        return byte;
    }
    return state.ioRegs[offset] ?? 0;
}
/** Sprint 48 ADR-0070 — ADC peripheral I/O offsets (cached). */
const IOREG_ADCL = 0x78 - IO_OFFSET;
const IOREG_ADCH = 0x79 - IO_OFFSET;
const IOREG_ADCSRA = 0x7a - IO_OFFSET;
const IOREG_ADMUX = 0x7c - IO_OFFSET;
/** ADCSRA bit masks (ATmega328P §24.9.2). */
const ADCSRA_ADEN = 1 << 7;
const ADCSRA_ADSC = 1 << 6;
const ADCSRA_ADIF = 1 << 4;
const ADCSRA_ADIE = 1 << 3;
/** Sprint 49 ADR-0071 — UART USART0 I/O offsets (cached). */
const IOREG_UCSR0A = 0xc0 - IO_OFFSET;
const IOREG_UCSR0B = 0xc1 - IO_OFFSET;
const IOREG_UBRR0L = 0xc4 - IO_OFFSET;
const IOREG_UBRR0H = 0xc5 - IO_OFFSET;
const IOREG_UDR0 = 0xc6 - IO_OFFSET;
/** UCSR0A bit masks (ATmega328P §19.10.2). */
const UCSR0A_RXC = 1 << 7;
const UCSR0A_TXC = 1 << 6;
const UCSR0A_UDRE = 1 << 5;
/** UCSR0B bit masks (ATmega328P §19.10.3). */
const UCSR0B_RXCIE = 1 << 7;
const UCSR0B_TXCIE = 1 << 6;
const UCSR0B_UDRIE = 1 << 5;
/**
 * Write an I/O register. PORTx writes also propagate to the PINx
 * shadow register because output high pins are visible on PINx
 * reads (see ATmega328P §13.2.2 "Reading the Pin Value").
 *
 * Sprint 48 ADR-0070 adds an ADC peripheral FSM branch on ADCSRA
 * writes — when ADEN+ADSC are asserted together, a fresh 10-bit
 * conversion is computed from `adcInputVoltages[ADMUX channel]`
 * against AVcc=5V and stored in ADCH:ADCL. ADIF flag is set; if
 * ADIE is also set, the ADC peripheral interrupt flag is raised.
 */
function writeIO(state, addr, value) {
    if (addr < IO_OFFSET || addr >= 0x100)
        return;
    const offset = addr - IO_OFFSET;
    state.ioRegs[offset] = value & 0xff;
    // Propagate PORTx → PINx shadow for output bits (DDRx=1 means output).
    if (offset === IOREG_PORTB) {
        const ddr = state.ioRegs[IOREG_DDRB] ?? 0;
        const pin = state.ioRegs[IOREG_PINB] ?? 0;
        state.ioRegs[IOREG_PINB] = (pin & ~ddr) | (value & ddr);
    }
    else if (offset === IOREG_PORTC) {
        const ddr = state.ioRegs[IOREG_DDRC] ?? 0;
        const pin = state.ioRegs[IOREG_PINC] ?? 0;
        state.ioRegs[IOREG_PINC] = (pin & ~ddr) | (value & ddr);
    }
    else if (offset === IOREG_PORTD) {
        const ddr = state.ioRegs[IOREG_DDRD] ?? 0;
        const pin = state.ioRegs[IOREG_PIND] ?? 0;
        state.ioRegs[IOREG_PIND] = (pin & ~ddr) | (value & ddr);
    }
    else if (offset === IOREG_ADCSRA) {
        // Sprint 48 ADR-0070 — ADC FSM on ADCSRA write.
        const adcEnabled = (value & ADCSRA_ADEN) !== 0;
        const adcStartConvert = (value & ADCSRA_ADSC) !== 0;
        if (adcEnabled && adcStartConvert) {
            // Read selected channel from ADMUX (low 4 bits = MUX3..MUX0).
            const admux = state.ioRegs[IOREG_ADMUX] ?? 0;
            const channel = admux & 0x0f;
            // Sprint 48 MVP: only single-ended channels 0..7 supported.
            if (channel < 8) {
                const vAdc = state.adcInputVoltages[channel] ?? 0;
                const vRef = 5.0; // AVcc, REFS0=1 default per Arduino convention
                const clamped = Math.max(0, Math.min(vRef, vAdc));
                const adcRaw = Math.round((clamped / vRef) * 1023);
                state.ioRegs[IOREG_ADCL] = adcRaw & 0xff;
                state.ioRegs[IOREG_ADCH] = (adcRaw >> 8) & 0x03;
                // Set ADIF flag.
                const adcsraNew = (value | ADCSRA_ADIF) & ~ADCSRA_ADSC;
                state.ioRegs[offset] = adcsraNew;
                // Trigger ADC interrupt if ADIE set.
                if ((adcsraNew & ADCSRA_ADIE) !== 0) {
                    state.peripheralInterruptFlags |= 1 << 2; // AVR_INTERRUPT_BITS.ADC
                }
            }
        }
    }
    else if (offset === IOREG_UCSR0B) {
        // Sprint 49 ADR-0071 — Mirror UART interrupt enable bits to
        // `peripheralInterruptEnable` so the post-opcode dispatch can
        // consult a single flat mask. RXCIE0/TXCIE0/UDRIE0 in UCSR0B
        // gate which UART event raises a real ISR jump.
        if ((value & UCSR0B_RXCIE) !== 0) {
            state.peripheralInterruptEnable |= 1 << 6; // USART_RX
        }
        else {
            state.peripheralInterruptEnable &= ~(1 << 6);
        }
        if ((value & UCSR0B_TXCIE) !== 0) {
            state.peripheralInterruptEnable |= 1 << 9; // USART_TX
        }
        else {
            state.peripheralInterruptEnable &= ~(1 << 9);
        }
        if ((value & UCSR0B_UDRIE) !== 0) {
            state.peripheralInterruptEnable |= 1 << 8; // USART_UDRE
        }
        else {
            state.peripheralInterruptEnable &= ~(1 << 8);
        }
    }
    else if (offset === IOREG_UDR0) {
        // Sprint 49 ADR-0071 — UART TX path on UDR0 write.
        state.uartTxBuffer.push(value & 0xff);
        const ucsr0bByte = state.ioRegs[IOREG_UCSR0B] ?? 0;
        // Idealized MVP: byte transmitted instantly.
        //   - Set TXC (transmit complete)
        //   - UDRE stays high (buffer immediately ready for next byte)
        state.ioRegs[IOREG_UCSR0A] =
            ((state.ioRegs[IOREG_UCSR0A] ?? 0) | UCSR0A_TXC | UCSR0A_UDRE) & 0xff;
        // Trigger USART_TX interrupt if TXCIE0 set.
        if ((ucsr0bByte & UCSR0B_TXCIE) !== 0) {
            state.peripheralInterruptFlags |= 1 << 9; // AVR_INTERRUPT_BITS.USART_TX
        }
        // Trigger USART_UDRE interrupt if UDRIE0 set.
        if ((ucsr0bByte & UCSR0B_UDRIE) !== 0) {
            state.peripheralInterruptFlags |= 1 << 8; // AVR_INTERRUPT_BITS.USART_UDRE
        }
    }
    else if (offset === IOREG_UBRR0L) {
        // Sprint 49 ADR-0071 — UART baud rate update on UBRR0L write.
        const ubrr0h = state.ioRegs[IOREG_UBRR0H] ?? 0;
        const ubrr0Combined = ((ubrr0h & 0x0f) << 8) | (value & 0xff);
        // F_CPU = 16 MHz default; baud = F_CPU / (16 * (UBRR + 1))
        state.uartBaudRate = 16_000_000 / (16 * (ubrr0Combined + 1));
    }
}
/**
 * Set a GPIO input bit. Used by the stamp's per-Newton-iteration
 * input read pass (Sprint 47-2): if the user wired a 5 V net to
 * pin PB0, the stamp calls `setGpioInput(state, 'B', 0, true)` so
 * the next `IN PINB` instruction reads HIGH for that pin.
 *
 * Only updates input bits (DDRx = 0). Output bits keep their
 * PORTx-derived value.
 */
export function setGpioInput(state, port, pinIdx, high) {
    const ddrAddr = port === "B" ? IOREG_DDRB : port === "C" ? IOREG_DDRC : IOREG_DDRD;
    const pinAddr = port === "B" ? IOREG_PINB : port === "C" ? IOREG_PINC : IOREG_PIND;
    const ddr = state.ioRegs[ddrAddr] ?? 0;
    // Only update if pin is configured as input (DDRx bit = 0).
    if ((ddr & (1 << pinIdx)) !== 0)
        return;
    const cur = state.ioRegs[pinAddr] ?? 0;
    if (high) {
        state.ioRegs[pinAddr] = cur | (1 << pinIdx);
    }
    else {
        state.ioRegs[pinAddr] = cur & ~(1 << pinIdx);
    }
}
/**
 * Read a GPIO output bit. Sprint 47-2 stamp uses this to drive each
 * pin's net voltage:
 *   pin output HIGH → V = vSupply (e.g. 5 V)
 *   pin output LOW  → V = 0
 *   pin input mode  → no voltage source stamped (high-Z, returns null)
 *
 * Returns `null` if the pin is configured as input (DDRx = 0); the
 * caller treats this as "do not stamp an augmented voltage source
 * row for this pin".
 */
export function getGpioOutput(state, port, pinIdx) {
    const ddrAddr = port === "B" ? IOREG_DDRB : port === "C" ? IOREG_DDRC : IOREG_DDRD;
    const portAddr = port === "B" ? IOREG_PORTB : port === "C" ? IOREG_PORTC : IOREG_PORTD;
    const ddr = state.ioRegs[ddrAddr] ?? 0;
    if ((ddr & (1 << pinIdx)) === 0)
        return null; // input mode
    const portReg = state.ioRegs[portAddr] ?? 0;
    return (portReg & (1 << pinIdx)) !== 0;
}
/** Update zero / negative / sign-derived SREG flags. Common helper. */
function updateZN(state, result) {
    const masked = result & 0xff;
    if (masked === 0)
        state.sreg |= 1 << SREG_Z;
    else
        state.sreg &= ~(1 << SREG_Z);
    if ((masked & 0x80) !== 0)
        state.sreg |= 1 << SREG_N;
    else
        state.sreg &= ~(1 << SREG_N);
}
/**
 * Internal opcode dispatch — decode + execute a single instruction.
 * Advances `state.pc` and `state.cycles`. Sets `state.halted` on
 * unsupported opcodes. Sprint 48 ADR-0070 wraps this with public
 * `stepInstruction` which adds post-opcode interrupt dispatch.
 *
 * Opcode dispatch follows the AVR Instruction Set Manual decoding
 * pattern: most-significant nibble of the 16-bit word selects the
 * top-level group, then sub-bits resolve the specific instruction.
 */
function dispatchOpcode(state) {
    if (state.halted)
        return;
    const word = state.flash[state.pc] ?? 0;
    state.pc += 1;
    state.instructions += 1;
    state.cycles += 1; // single-cycle baseline; multi-cycle ops add below
    // NOP — 0x0000.
    if (word === 0x0000)
        return;
    // LDI Rd, K — 1110 KKKK dddd KKKK (d in 16..31).
    if ((word & 0xf000) === 0xe000) {
        const d = ((word >> 4) & 0x0f) + 16;
        const k = ((word & 0x0f00) >> 4) | (word & 0x000f);
        state.regs[d] = k;
        return;
    }
    // OUT A, Rr — 1011 1AAr rrrr AAAA (A in 0..63 → I/O 0x20..0x5F).
    if ((word & 0xf800) === 0xb800) {
        const a = ((word & 0x0600) >> 5) | (word & 0x000f);
        const r = (word & 0x01f0) >> 4;
        writeIO(state, a + IO_OFFSET, state.regs[r] ?? 0);
        return;
    }
    // IN Rd, A — 1011 0AAd dddd AAAA.
    if ((word & 0xf800) === 0xb000) {
        const a = ((word & 0x0600) >> 5) | (word & 0x000f);
        const d = (word & 0x01f0) >> 4;
        state.regs[d] = readIO(state, a + IO_OFFSET);
        return;
    }
    // RJMP k — 1100 kkkk kkkk kkkk (k is 12-bit signed word offset).
    if ((word & 0xf000) === 0xc000) {
        let k = word & 0x0fff;
        if (k & 0x0800)
            k = k - 0x1000; // sign-extend 12-bit
        state.pc = (state.pc + k) & 0xffff;
        state.cycles += 1; // RJMP is 2 cycles
        return;
    }
    // RCALL k — 1101 kkkk kkkk kkkk.
    if ((word & 0xf000) === 0xd000) {
        let k = word & 0x0fff;
        if (k & 0x0800)
            k = k - 0x1000;
        // Push PC (2 bytes, little-endian).
        state.sram[state.sp] = state.pc & 0xff;
        state.sp -= 1;
        state.sram[state.sp] = (state.pc >> 8) & 0xff;
        state.sp -= 1;
        state.pc = (state.pc + k) & 0xffff;
        state.cycles += 2; // RCALL is 3 cycles total
        return;
    }
    // RET — 1001 0101 0000 1000.
    if (word === 0x9508) {
        state.sp += 1;
        const pcHi = state.sram[state.sp] ?? 0;
        state.sp += 1;
        const pcLo = state.sram[state.sp] ?? 0;
        state.pc = (pcHi << 8) | pcLo;
        state.cycles += 3; // RET is 4 cycles total
        return;
    }
    // ADD Rd, Rr — 0000 11rd dddd rrrr.
    if ((word & 0xfc00) === 0x0c00) {
        const r = ((word & 0x0200) >> 5) | (word & 0x000f);
        const d = (word & 0x01f0) >> 4;
        const a = state.regs[d] ?? 0;
        const b = state.regs[r] ?? 0;
        const sum = a + b;
        if (sum > 0xff)
            state.sreg |= 1 << SREG_C;
        else
            state.sreg &= ~(1 << SREG_C);
        state.regs[d] = sum & 0xff;
        updateZN(state, sum);
        return;
    }
    // SUB Rd, Rr — 0001 10rd dddd rrrr.
    if ((word & 0xfc00) === 0x1800) {
        const r = ((word & 0x0200) >> 5) | (word & 0x000f);
        const d = (word & 0x01f0) >> 4;
        const a = state.regs[d] ?? 0;
        const b = state.regs[r] ?? 0;
        const diff = a - b;
        if (diff < 0)
            state.sreg |= 1 << SREG_C;
        else
            state.sreg &= ~(1 << SREG_C);
        state.regs[d] = diff & 0xff;
        updateZN(state, diff);
        return;
    }
    // SUBI Rd, K — 0101 KKKK dddd KKKK (d in 16..31).
    if ((word & 0xf000) === 0x5000) {
        const d = ((word >> 4) & 0x0f) + 16;
        const k = ((word & 0x0f00) >> 4) | (word & 0x000f);
        const a = state.regs[d] ?? 0;
        const diff = a - k;
        if (diff < 0)
            state.sreg |= 1 << SREG_C;
        else
            state.sreg &= ~(1 << SREG_C);
        state.regs[d] = diff & 0xff;
        updateZN(state, diff);
        return;
    }
    // INC Rd — 1001 010d dddd 0011.
    if ((word & 0xfe0f) === 0x9403) {
        const d = (word & 0x01f0) >> 4;
        const a = (state.regs[d] ?? 0) + 1;
        state.regs[d] = a & 0xff;
        updateZN(state, a);
        return;
    }
    // DEC Rd — 1001 010d dddd 1010.
    if ((word & 0xfe0f) === 0x940a) {
        const d = (word & 0x01f0) >> 4;
        const a = (state.regs[d] ?? 0) - 1;
        state.regs[d] = a & 0xff;
        updateZN(state, a);
        return;
    }
    // MOV Rd, Rr — 0010 11rd dddd rrrr.
    if ((word & 0xfc00) === 0x2c00) {
        const r = ((word & 0x0200) >> 5) | (word & 0x000f);
        const d = (word & 0x01f0) >> 4;
        state.regs[d] = state.regs[r] ?? 0;
        return;
    }
    // EOR Rd, Rr — 0010 01rd dddd rrrr.
    if ((word & 0xfc00) === 0x2400) {
        const r = ((word & 0x0200) >> 5) | (word & 0x000f);
        const d = (word & 0x01f0) >> 4;
        const a = state.regs[d] ?? 0;
        const b = state.regs[r] ?? 0;
        const result = a ^ b;
        state.regs[d] = result;
        updateZN(state, result);
        state.sreg &= ~(1 << SREG_V);
        return;
    }
    // AND Rd, Rr — 0010 00rd dddd rrrr.
    if ((word & 0xfc00) === 0x2000) {
        const r = ((word & 0x0200) >> 5) | (word & 0x000f);
        const d = (word & 0x01f0) >> 4;
        const a = state.regs[d] ?? 0;
        const b = state.regs[r] ?? 0;
        const result = a & b;
        state.regs[d] = result;
        updateZN(state, result);
        return;
    }
    // OR Rd, Rr — 0010 10rd dddd rrrr.
    if ((word & 0xfc00) === 0x2800) {
        const r = ((word & 0x0200) >> 5) | (word & 0x000f);
        const d = (word & 0x01f0) >> 4;
        const a = state.regs[d] ?? 0;
        const b = state.regs[r] ?? 0;
        const result = a | b;
        state.regs[d] = result;
        updateZN(state, result);
        return;
    }
    // BRNE k — 1111 01kk kkkk k001 (branch if Z = 0, k 7-bit signed).
    if ((word & 0xfc07) === 0xf401) {
        let k = (word >> 3) & 0x7f;
        if (k & 0x40)
            k = k - 0x80; // sign-extend 7-bit
        if ((state.sreg & (1 << SREG_Z)) === 0) {
            state.pc = (state.pc + k) & 0xffff;
            state.cycles += 1;
        }
        return;
    }
    // BREQ k — 1111 00kk kkkk k001 (branch if Z = 1).
    if ((word & 0xfc07) === 0xf001) {
        let k = (word >> 3) & 0x7f;
        if (k & 0x40)
            k = k - 0x80;
        if ((state.sreg & (1 << SREG_Z)) !== 0) {
            state.pc = (state.pc + k) & 0xffff;
            state.cycles += 1;
        }
        return;
    }
    // BRCS / BRLO k — 1111 00kk kkkk k000 (branch if C = 1).
    if ((word & 0xfc07) === 0xf000) {
        let k = (word >> 3) & 0x7f;
        if (k & 0x40)
            k = k - 0x80;
        if ((state.sreg & (1 << SREG_C)) !== 0) {
            state.pc = (state.pc + k) & 0xffff;
            state.cycles += 1;
        }
        return;
    }
    // BRCC / BRSH k — 1111 01kk kkkk k000 (branch if C = 0).
    if ((word & 0xfc07) === 0xf400) {
        let k = (word >> 3) & 0x7f;
        if (k & 0x40)
            k = k - 0x80;
        if ((state.sreg & (1 << SREG_C)) === 0) {
            state.pc = (state.pc + k) & 0xffff;
            state.cycles += 1;
        }
        return;
    }
    // SBI A, b — 1001 1010 AAAA Abbb (set bit b in I/O reg A, A in 0..31).
    if ((word & 0xff00) === 0x9a00) {
        const a = (word >> 3) & 0x1f;
        const b = word & 0x07;
        const cur = readIO(state, a + IO_OFFSET);
        writeIO(state, a + IO_OFFSET, cur | (1 << b));
        state.cycles += 1;
        return;
    }
    // CBI A, b — 1001 1000 AAAA Abbb (clear bit b in I/O reg A).
    if ((word & 0xff00) === 0x9800) {
        const a = (word >> 3) & 0x1f;
        const b = word & 0x07;
        const cur = readIO(state, a + IO_OFFSET);
        writeIO(state, a + IO_OFFSET, cur & ~(1 << b));
        state.cycles += 1;
        return;
    }
    // PUSH Rr — 1001 001r rrrr 1111.
    if ((word & 0xfe0f) === 0x920f) {
        const r = (word & 0x01f0) >> 4;
        state.sram[state.sp] = state.regs[r] ?? 0;
        state.sp -= 1;
        state.cycles += 1;
        return;
    }
    // POP Rd — 1001 000d dddd 1111.
    if ((word & 0xfe0f) === 0x900f) {
        const d = (word & 0x01f0) >> 4;
        state.sp += 1;
        state.regs[d] = state.sram[state.sp] ?? 0;
        state.cycles += 1;
        return;
    }
    // Sprint 48 ADR-0070 — SEI (Set Global Interrupt Flag).
    // 1001 0100 0111 1000 = 0x9478.
    if (word === 0x9478) {
        state.sreg |= 1 << SREG_I;
        return;
    }
    // Sprint 48 ADR-0070 — CLI (Clear Global Interrupt Flag).
    // 1001 0100 1111 1000 = 0x94f8.
    if (word === 0x94f8) {
        state.sreg &= ~(1 << SREG_I);
        return;
    }
    // Sprint 48 ADR-0070 — RETI (Return from Interrupt).
    // 1001 0101 0001 1000 = 0x9518. Pop PC, then SET I.
    if (word === 0x9518) {
        state.sp += 1;
        const pcHi = state.sram[state.sp] ?? 0;
        state.sp += 1;
        const pcLo = state.sram[state.sp] ?? 0;
        state.pc = (pcHi << 8) | pcLo;
        state.sreg |= 1 << SREG_I;
        state.cycles += 3; // RETI is 4 cycles total
        return;
    }
    // Sprint 48 ADR-0070 — LDS Rd, k (Load Direct from Data Space).
    // 1001 000d dddd 0000 + kkkk kkkk kkkk kkkk (16-bit address).
    // Two-word instruction; second word is the data memory address.
    // AVR data memory map: 0x00..0x1F GP regs, 0x20..0xFF I/O space,
    // 0x100..0x8FF SRAM (ATmega328P 2KB).
    if ((word & 0xfe0f) === 0x9000) {
        const d = (word & 0x01f0) >> 4;
        const addr = state.flash[state.pc] ?? 0;
        state.pc += 1;
        if (addr >= 0x100 && addr < state.sram.length) {
            state.regs[d] = state.sram[addr] ?? 0;
        }
        else if (addr >= IO_OFFSET && addr < 0x100) {
            state.regs[d] = readIO(state, addr);
        }
        else if (addr < 0x20) {
            state.regs[d] = state.regs[addr] ?? 0;
        }
        else {
            state.regs[d] = 0;
        }
        state.cycles += 1; // LDS is 2 cycles
        return;
    }
    // Sprint 48 ADR-0070 — STS k, Rr (Store Direct to Data Space).
    // 1001 001r rrrr 0000 + kkkk kkkk kkkk kkkk.
    if ((word & 0xfe0f) === 0x9200) {
        const r = (word & 0x01f0) >> 4;
        const addr = state.flash[state.pc] ?? 0;
        state.pc += 1;
        const value = state.regs[r] ?? 0;
        if (addr >= 0x100 && addr < state.sram.length) {
            state.sram[addr] = value;
        }
        else if (addr >= IO_OFFSET && addr < 0x100) {
            writeIO(state, addr, value);
        }
        else if (addr < 0x20) {
            state.regs[addr] = value;
        }
        state.cycles += 1; // STS is 2 cycles
        return;
    }
    // Unknown opcode — halt and report.
    state.halted = true;
    state.lastUnknownOpcode = word;
}
/**
 * Find the lowest set bit (0..31) of a 32-bit integer. Returns -1
 * if all bits are zero. Sprint 48 ADR-0070 interrupt priority helper.
 */
function lowestSetBit(mask) {
    if (mask === 0)
        return -1;
    // De Bruijn-like simple loop is fine for 8-bit interrupt set.
    for (let i = 0; i < 32; i += 1) {
        if ((mask & (1 << i)) !== 0)
            return i;
    }
    return -1;
}
/**
 * Public step function — Sprint 48 ADR-0070. Calls the private
 * opcode dispatcher, then checks pending interrupts.
 *
 * If `SREG.I` is set AND `peripheralInterruptFlags & peripheralInterruptEnable`
 * is non-zero, the lowest-priority pending interrupt is taken:
 *
 *   1. PC is pushed (2 bytes, big-endian high-first per AVR convention).
 *   2. PC is set to the ISR vector address.
 *   3. SREG.I is cleared (auto-disable nested interrupts).
 *   4. The pending flag bit is cleared (MVP — full hardware behavior
 *      is per-peripheral; some auto-clear, others require manual
 *      clear via SBI on the flag bit. Sprint 50+ extends).
 */
export function stepInstruction(state) {
    dispatchOpcode(state);
    if (state.halted)
        return;
    if ((state.sreg & (1 << SREG_I)) === 0)
        return;
    const pending = state.peripheralInterruptFlags & state.peripheralInterruptEnable;
    if (pending === 0)
        return;
    const bitIndex = lowestSetBit(pending);
    const vector = AVR_INTERRUPT_VECTORS[bitIndex];
    if (vector === undefined)
        return;
    // Push PC (2 bytes — high then low, AVR convention §3.3).
    state.sram[state.sp] = state.pc & 0xff;
    state.sp -= 1;
    state.sram[state.sp] = (state.pc >> 8) & 0xff;
    state.sp -= 1;
    // Jump to ISR vector.
    state.pc = vector;
    // Auto-disable global interrupts (re-enabled by RETI).
    state.sreg &= ~(1 << SREG_I);
    // Clear the consumed pending flag bit (MVP).
    state.peripheralInterruptFlags &= ~(1 << bitIndex);
    // ISR latency 4 cycles (datasheet §11.1 — push + vector + entry).
    state.cycles += 4;
}
/**
 * Sprint 48 ADR-0070 — Interrupt source bit positions.
 * Maps to `peripheralInterruptFlags` / `peripheralInterruptEnable`
 * bit indices. The `AVR_INTERRUPT_VECTORS` array (below) is indexed
 * by these bit positions to dispatch to the correct ISR address.
 */
export const AVR_INTERRUPT_BITS = {
    INT0: 0,
    INT1: 1,
    ADC: 2,
    TWI: 3,
    SPI_STC: 4,
    TIMER0_OVF: 5,
    USART_RX: 6,
    TIMER1_COMPA: 7,
    // Sprint 49 ADR-0071 — UART additions.
    USART_UDRE: 8,
    USART_TX: 9,
};
/**
 * Sprint 48 ADR-0070 — ISR vector table (ATmega328P §11.4).
 * Indexed by interrupt bit position (`AVR_INTERRUPT_BITS`). Word
 * addresses (multiply by 2 for byte address per AVR convention).
 */
const AVR_INTERRUPT_VECTORS = [
    0x0001, // [0] INT0  (byte 0x0002 → word 0x0001)
    0x0002, // [1] INT1  (byte 0x0004 → word 0x0002)
    0x0015, // [2] ADC   (byte 0x002A → word 0x0015)
    0x0018, // [3] TWI   (byte 0x0030 → word 0x0018)
    0x0011, // [4] SPI_STC (byte 0x0022 → word 0x0011)
    0x0010, // [5] TIMER0_OVF (byte 0x0020 → word 0x0010)
    0x0012, // [6] USART_RX (byte 0x0024 → word 0x0012)
    0x000b, // [7] TIMER1_COMPA (byte 0x0016 → word 0x000B)
    0x0013, // [8] USART_UDRE (byte 0x0026 → word 0x0013)
    0x0014, // [9] USART_TX (byte 0x0028 → word 0x0014)
];
/**
 * Sprint 48 ADR-0070 helper — set/clear an interrupt enable bit by
 * source name. Sentinel runner uses this to permit ISR dispatch.
 */
export function setInterruptEnable(state, bit, enabled) {
    const idx = AVR_INTERRUPT_BITS[bit];
    if (enabled) {
        state.peripheralInterruptEnable |= 1 << idx;
    }
    else {
        state.peripheralInterruptEnable &= ~(1 << idx);
    }
}
/**
 * Sprint 48 ADR-0070 helper — raise a peripheral interrupt flag by
 * source name. Sensor stamps / sentinel runner trigger ISRs via this.
 */
export function raisePeripheralInterrupt(state, bit) {
    const idx = AVR_INTERRUPT_BITS[bit];
    state.peripheralInterruptFlags |= 1 << idx;
}
/**
 * Sprint 48 ADR-0070 helper — set an ADC channel input voltage.
 * Sensor stamps / sentinel runner write the analog level seen by
 * each ADC channel; the ADC FSM (fired on ADCSRA.ADSC write) reads
 * this to compute the 10-bit conversion result.
 */
export function setAdcInputVoltage(state, channel, voltage) {
    if (channel < 0 || channel >= state.adcInputVoltages.length) {
        throw new Error(`setAdcInputVoltage: channel ${String(channel)} out of range (0..${String(state.adcInputVoltages.length - 1)})`);
    }
    state.adcInputVoltages[channel] = voltage;
}
/**
 * Sprint 49 ADR-0071 — Push a byte into the UART RX FIFO, simulating
 * an external sender (loopback / sensor / serial console). Sets
 * `RXC` flag in UCSR0A and raises USART_RX peripheral interrupt if
 * `RXCIE0` is enabled in UCSR0B.
 */
export function pushUartRx(state, byte) {
    state.uartRxBuffer.push(byte & 0xff);
    state.ioRegs[IOREG_UCSR0A] = ((state.ioRegs[IOREG_UCSR0A] ?? 0) | UCSR0A_RXC) & 0xff;
    const ucsr0b = state.ioRegs[IOREG_UCSR0B] ?? 0;
    if ((ucsr0b & UCSR0B_RXCIE) !== 0) {
        state.peripheralInterruptFlags |= 1 << AVR_INTERRUPT_BITS.USART_RX;
    }
}
/**
 * Sprint 49 ADR-0071 — Pop the oldest byte from the UART TX FIFO.
 * Sentinel runner / external observer reads transmitted bytes here.
 * Returns `undefined` if the buffer is empty.
 */
export function popUartTx(state) {
    return state.uartTxBuffer.shift();
}
/**
 * Sprint 49 ADR-0071 — Set UART baud rate directly (testing helper;
 * Arduino programs usually configure via UBRR0L write). At F_CPU =
 * 16 MHz, `Serial.begin(9600)` corresponds to UBRR0 = 103.
 */
export function setUartBaudRate(state, baud) {
    if (baud <= 0) {
        throw new Error(`setUartBaudRate: baud must be positive, got ${String(baud)}`);
    }
    state.uartBaudRate = baud;
}
/**
 * Step N instructions or until halted. Used by the Sprint 47-2 stamp
 * to advance the emulator one solver tick worth of execution. Returns
 * the number of instructions actually retired (≤ count if halted).
 */
export function stepN(state, count) {
    let i = 0;
    for (; i < count; i += 1) {
        if (state.halted)
            break;
        stepInstruction(state);
    }
    return i;
}
/**
 * Diagnostic helper — read the I/O register at a given data memory
 * address. Used by sentinel tests + Inspector runtime panel.
 */
export function readIORegister(state, addr) {
    return readIO(state, addr);
}
/**
 * Diagnostic helper — write the I/O register at a given data memory
 * address. Used by tests to seed PINx values when emulating an
 * external GPIO drive (Sprint 47 stamp uses `setGpioInput` instead).
 */
export function writeIORegister(state, addr, value) {
    writeIO(state, addr, value);
}
/** Constants exported for sentinel + integration tests. */
export const AVR_IO_ADDRESSES = {
    PINB: 0x23,
    DDRB: 0x24,
    PORTB: 0x25,
    PINC: 0x26,
    DDRC: 0x27,
    PORTC: 0x28,
    PIND: 0x29,
    DDRD: 0x2a,
    PORTD: 0x2b,
    TCCR0A: 0x44,
    TCNT0: 0x46,
    OCR0A: 0x47,
    OCR0B: 0x48,
    ADCL: 0x78,
    ADCH: 0x79,
    ADCSRA: 0x7a,
    ADMUX: 0x7c,
    // Sprint 49 ADR-0071 — UART USART0 register set (additive).
    UCSR0A: 0xc0,
    UCSR0B: 0xc1,
    UCSR0C: 0xc2,
    UBRR0L: 0xc4,
    UBRR0H: 0xc5,
    UDR0: 0xc6,
};
//# sourceMappingURL=avr-emulator.js.map